/**
 * API actions
 */
import axios from 'axios';

import { selectCurrentApi } from '../selectors/apiSelectors';
// import authenticationService from '../services/authenticationService';

export const SELECT_API = 'redux/api/SELECT_API';
export const UPDATE_FIELD = 'redux/api/UPDATE_FIELD';

export const LOAD_RESULTS = 'redux/api/LOAD_RESULTS';
export const LOAD_RESULTS_SUCCESS = 'redux/api/LOAD_RESULTS_SUCCESS';
export const LOAD_RESULTS_FAILURE = 'redux/api/LOAD_RESULTS_FAILURE';

/**
 * Dispatched when we want to set the current api module.
 *
 * @param {String | Number} apiId - The API module Id.
 * @return {Object} - An action object with a type of SELECT_API
 */
export function selectApi(apiId) {
	return {
		type: SELECT_API,
		payload: apiId,
	};
}

/**
 * Dispatched when we want to set the value of a field of the currently selected API.
 *
 * @param {String | Number} apiId - The API field Id.
 * @param {String} value - The value of the field.
 * @return {Object} - An action object with a type of UPDATE_FIELD
 */
export function updateField(fieldId, value) {
	return {
		type: UPDATE_FIELD,
		payload: {
			fieldId,
			value,
		},
	};
}

/**
 * Determines the correct endpoint and method to hit for the current API.
 *
 * @param {String | Number} id - The Id of current the API.
 * @param {Array} fields - Fields with values of the current API.
 * @returns {Object} An object containing the url and method.
 */
const determineApiEndpoint = (id, fields) => {
	// Decide where to send our fields
	// TODO: Configure additional API's here
	let url, method;
	switch (id) {
		case 'suggestion': {
			const { value } = fields.find((field) => field.id === 'contentId');
			if (value && value.length) {
				url = `/api/suggestion/${value}`;
				method = 'GET';
			} else {
				url = `/api/suggestion`;
				method = 'POST';
			}
			break;
		}
		case 'searchV2': {
			url = `/api/search/v2`;
			method = 'POST';
			break;
		}
		case 'proximityV2': {
			url = `/api/recommendations/proximity/v2`;
			method = 'GET';
			break;
		}
		case 'containsV2': {
			url = `/api/recommendations/contains/v2`;
			method = 'POST';
			break;
		}
		case 'aggregatesV2': {
			url = `/api/recommendations/aggregates/v2`;
			method = `POST`;
			break;
		}
		case 'aggregatesV3Get': {
			url = `/api/recommendations/aggregates/get/v3`;
			method = `POST`;
			break;
		}
		default:
			throw Error(`Unknown API ${id}`);
	}

	return { url, method };
};

/**
 * Parses the response for the current API.
 *
 * @param {String | Number} id - The Id of current the API.
 * @param {Object} response - The web response.
 * @returns {Object} An object containing the data and totalResults
 */
const parseApiResponse = (id, response) => {
	// TODO: Configure additional API's here
	let data, totalResults, facets, camPayload;
	switch (id) {
		case 'suggestion': {
			data = response.data.terms;
			totalResults = response.data.totalTerms;
			camPayload = response.data.camPayload;
			break;
		}
		case 'searchV2': {
			data = response.data.results;
			totalResults = response.data.totalResults;
			facets = response.data.facets;
			break;
		}
		case 'proximityV2': {
			data = response.data.recommendations;
			totalResults = response.data.totalResults;
			break;
		}
		case 'aggregatesV2': {
			data = response.data.blocks;
			totalResults = response.data.totalResults;
			break;
		}
		case 'containsV2': {
			data = response.data.recommendations;
			totalResults = response.data.totalResults;
			break;
		}
		case 'aggregatesV3Get': {
			data = response.data.recommendations;
			totalResults = response.data.totalResults;
			break;
		}
		default:
			throw Error(`Unknown API ${id}`);
	}

	return { data, totalResults, facets, camPayload };
};

/**
 * Dispatched when we want to fetch results from the current API.
 *
 * @return {Object} - A thunk.
 */
export function submitApiRequest(accessToken) {
	return async (dispatch, getState) => {
		const currentApi = selectCurrentApi(getState());
		const { fields, id } = currentApi;
		const config = determineApiEndpoint(id, fields);

		// Package up our fields
		config.headers = { Authorization: `Bearer ${accessToken}` };
		config.data = {};
		config.params = {};
		for (const { id, value } of fields) {
			if (config.method === 'GET') {
				if (value !== null && Array.isArray(value) && value.length > 0) {
					config.params[id] = value.join(','); // join arrays as csvs
				} else {
					config.params[id] = value;
				}
			} else {
				if (value !== null && (typeof value !== 'string' || value.length > 0)) {
					config.data[id] = value;
				}
			}
		}

		// Let the redux store know we're doing something
		dispatch({
			type: LOAD_RESULTS,
		});

		try {
			// Load some results
			const start = new Date().getTime();
			const response = await axios(config);
			const stop = new Date().getTime();

			// Parse the response
			const parsedResponse = parseApiResponse(id, response);

			// Let the store know we're done!
			dispatch({
				type: LOAD_RESULTS_SUCCESS,
				payload: {
					...parsedResponse,
					timeTaken: (stop - start) / 1000,
				},
			});
		} catch (error) {
			dispatch({
				type: LOAD_RESULTS_FAILURE,
				payload: {
					response: error.response,
					request: error.request,
					message: error.message,
					name: error.name,
					stack: error.stack,
				},
			});
		}
	};
}
